import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Modal,
  Box,
  TextField,
  Avatar,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Container,
  Chip,
  Card,
  CardContent,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { 
  Add, 
  Delete, 
  Edit, 
  Logout, 
  Dashboard, 
  Person, 
  Email, 
  Business, 
  Lock,
  PhotoCamera,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  ArrowBack,
  Search,
} from "@mui/icons-material";
import { ref, get, child, remove, update } from "firebase/database";
import { auth, database, storage } from "../firebase/Config";
import { uploadBytes, getDownloadURL, ref as storageRef } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

// Estilos para o modal
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  p: 4,
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    photo: null,
    password: "",
    role: "user",
    isActive: true,
  }); 
  const [photoPreview, setPhotoPreview] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const navigate = useNavigate();
  
  // Estados para validação e feedback
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    userId: null,
    userName: "",
  });

  // Função Firebase para criar novo usuário via backend (Functions)
  const functions = getFunctions();
  const cadastrarUsuario = httpsCallable(functions, 'cadastrarUsuario'); // Função Firebase

  // Buscar o e-mail do usuário logado
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  // Funções de validação
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!newUser.firstName.trim()) {
      newErrors.firstName = "Nome é obrigatório";
    } else if (newUser.firstName.length < 2) {
      newErrors.firstName = "Nome deve ter pelo menos 2 caracteres";
    }

    if (!newUser.lastName.trim()) {
      newErrors.lastName = "Sobrenome é obrigatório";
    } else if (newUser.lastName.length < 2) {
      newErrors.lastName = "Sobrenome deve ter pelo menos 2 caracteres";
    }

    if (!isEditing) {
      if (!newUser.email.trim()) {
        newErrors.email = "Email é obrigatório";
      } else if (!validateEmail(newUser.email)) {
        newErrors.email = "Email inválido";
      }

      if (!newUser.password.trim()) {
        newErrors.password = "Senha é obrigatória";
      } else if (newUser.password.length < 6) {
        newErrors.password = "Senha deve ter pelo menos 6 caracteres";
      }
    }

    if (!newUser.phone.trim()) {
      newErrors.phone = "Telefone é obrigatório";
    }

    if (!newUser.company.trim()) {
      newErrors.company = "Nome da empresa é obrigatório";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Função para abrir/fechar modal
  const handleOpen = () => {
    setPhotoPreview(null); 
    setNewUser({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      photo: null,
      password: "",
      role: "user",
      isActive: true,
    }); 
    setErrors({});
    setIsEditing(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setErrors({});
    setPhotoPreview(null);
  };

  // Função para mostrar snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Função para buscar usuários do Realtime Database
  const fetchUsers = async () => {
    setLoading(true);
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, "users"));
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const usersArray = Object.keys(usersData).map((key) => ({
          id: key,
          ...usersData[key],
        }));
        setUsers(usersArray);
        setFilteredUsers(usersArray);
      }
    } catch (error) {
      console.error("Erro ao buscar usuários: ", error);
    }
    setLoading(false);
  };

  // Função para adicionar novo usuário via Firebase Functions
  const handleAddUser = async () => {
    if (!validateForm()) {
      showSnackbar("Por favor, corrija os erros no formulário", "error");
      return;
    }

    setSubmitLoading(true);
    try {
      // Verificar se email já existe
      const dbRef = ref(database);
      const snapshot = await get(child(dbRef, "users"));
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const emailExists = Object.values(usersData).some(
          (user) => user.email === newUser.email
        );
        if (emailExists) {
          showSnackbar("Este email já está cadastrado", "error");
          setErrors({ ...errors, email: "Email já cadastrado" });
          setSubmitLoading(false);
          return;
        }
      }

      // Chamando a function do backend para criar o usuário
      const result = await cadastrarUsuario({
        firstName: newUser.firstName.trim(),
        lastName: newUser.lastName.trim(),
        email: newUser.email.trim().toLowerCase(),
        phone: newUser.phone.trim(),
        company: newUser.company.trim(),
        password: newUser.password,
        photo: newUser.photo ? await handleUploadPhoto() : null,
        role: newUser.role,
        isActive: newUser.isActive,
      });

      console.log(result.data.message);
      showSnackbar("Usuário cadastrado com sucesso!", "success");
      fetchUsers();
      handleClose();
    } catch (error) {
      console.error("Erro ao criar usuário: ", error);
      const errorMessage = error.message || "Erro ao cadastrar usuário";
      showSnackbar(errorMessage, "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Função para upload da foto e retorno da URL
  const handleUploadPhoto = async () => {
    if (!newUser.photo) return null;

    const userId = new Date().getTime(); // Simulando ID para nome do arquivo
    const photoRef = storageRef(storage, `users/${userId}/profile.jpg`);
    await uploadBytes(photoRef, newUser.photo);
    const photoURL = await getDownloadURL(photoRef);
    return photoURL;
  };

  // Função para editar um usuário
  const handleEditUser = (user) => {
    setIsEditing(true);
    setEditingUser(user);
    setNewUser({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phone: user.phone || "",
      company: user.company || "",
      photo: null,
      password: "",
      role: user.role || "user",
      isActive: user.isActive !== undefined ? user.isActive : true,
    });
    setPhotoPreview(user.photoURL || null);
    setErrors({});
    setOpen(true);
  };

  // Função para salvar alterações do usuário
  const handleSaveEditUser = async () => {
    if (!validateForm()) {
      showSnackbar("Por favor, corrija os erros no formulário", "error");
      return;
    }

    setSubmitLoading(true);
    try {
      const userId = editingUser.id;
      let photoURL = editingUser.photoURL;

      if (newUser.photo) {
        const photoRef = storageRef(storage, `users/${userId}/profile.jpg`);
        await uploadBytes(photoRef, newUser.photo);
        photoURL = await getDownloadURL(photoRef);
      }

      photoURL = photoURL || editingUser.photoURL || ""; 

      await update(ref(database, `users/${userId}`), {
        firstName: newUser.firstName.trim(),
        lastName: newUser.lastName.trim(),
        phone: newUser.phone.trim(),
        company: newUser.company.trim(),
        photoURL: photoURL,
        role: newUser.role,
        isActive: newUser.isActive,
      });

      showSnackbar("Usuário atualizado com sucesso!", "success");
      fetchUsers();
      handleClose();
    } catch (error) {
      console.error("Erro ao atualizar usuário: ", error);
      showSnackbar("Erro ao atualizar usuário", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Função para abrir diálogo de confirmação de exclusão
  const handleOpenDeleteDialog = (user) => {
    setDeleteDialog({
      open: true,
      userId: user.id,
      userName: `${user.firstName} ${user.lastName}`,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, userId: null, userName: "" });
  };

  // Função para filtrar usuários com base no termo de pesquisa
  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    if (value === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const email = user.email.toLowerCase();
        const company = (user.company || '').toLowerCase();
        
        return (
          fullName.includes(value) ||
          email.includes(value) ||
          company.includes(value)
        );
      });
      setFilteredUsers(filtered);
    }
  };

  // Limpar pesquisa
  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredUsers(users);
  };

  // Função para deletar um usuário
  const handleDeleteUser = async () => {
    try {
      await remove(ref(database, `users/${deleteDialog.userId}`));
      showSnackbar("Usuário excluído com sucesso!", "success");
      fetchUsers();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Erro ao deletar usuário: ", error);
      showSnackbar("Erro ao excluir usuário", "error");
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Validar tipo de arquivo
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        showSnackbar("Por favor, selecione uma imagem válida (JPG, PNG ou GIF)", "error");
        return;
      }

      // Validar tamanho (máx 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar("A imagem deve ter no máximo 5MB", "error");
        return;
      }

      setNewUser({ ...newUser, photo: file });
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    fetchUsers(); 
  }, []);

  const columns = [
    {
      field: "photoURL",
      headerName: "Foto",
      width: 100,
      renderCell: (params) => (
        <Avatar 
          src={params.value} 
          alt="Foto do usuário"
          sx={{ 
            width: 45, 
            height: 45,
            border: '2px solid #667eea',
          }}
        />
      ),
    },
    { field: "firstName", headerName: "Nome", width: 150 },
    { field: "lastName", headerName: "Sobrenome", width: 150 },
    { field: "email", headerName: "Email", width: 250 },
    { field: "phone", headerName: "Telefone", width: 150 },
    { field: "company", headerName: "Empresa", width: 150 },
    {
      field: "role",
      headerName: "Tipo",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value === 'admin' ? 'Administrador' : 'Usuário'}
          size="small"
          sx={{
            bgcolor: params.value === 'admin' ? '#667eea' : '#48bb78',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Ativo' : 'Inativo'}
          size="small"
          sx={{
            bgcolor: params.value ? '#48bb78' : '#e53935',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Editar usuário">
            <IconButton 
              onClick={() => handleEditUser(params.row)}
              sx={{ 
                color: '#667eea',
                '&:hover': { bgcolor: '#667eea20' }
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir usuário">
            <IconButton 
              onClick={() => handleOpenDeleteDialog(params.row)}
              sx={{ 
                color: '#e53935',
                '&:hover': { bgcolor: '#e5393520' }
              }}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton 
            onClick={() => navigate('/home')}
            sx={{ 
              color: '#fff', 
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Dashboard sx={{ mr: 2, fontSize: 32 }} />
          <Typography 
            variant="h5" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Gerenciamento de Usuários
          </Typography>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#667eea' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton 
                onClick={handleLogout}
                sx={{ 
                  color: '#fff',
                  '&:hover': { 
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
        <Fade in timeout={600}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#2d3748',
                      mb: 1,
                    }}
                  >
                    <Person sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }} />
                    Lista de Usuários
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    Total de usuários cadastrados: <strong>{users.length}</strong>
                    {searchTerm && ` | Filtrados: ${filteredUsers.length}`}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Add />}
                  onClick={handleOpen}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  Cadastrar Novo Usuário
                </Button>
              </Box>

              {/* Barra de Pesquisa */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Pesquisar por nome, email ou empresa..."
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#718096' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={handleClearSearch}
                          size="small"
                          sx={{ color: '#718096' }}
                        >
                          <Close />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f7fafc',
                      '&:hover': {
                        bgcolor: '#edf2f7',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#fff',
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={filteredUsers}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  checkboxSelection
                  disableSelectionOnClick
                  loading={loading}
                  onRowClick={(params) => navigate(`/users/${params.row.id}`)}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row': {
                      cursor: 'pointer',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: '#f7fafc',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: '#f7fafc',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* Modal de Cadastro/Edição */}
        <Modal open={open} onClose={handleClose}>
          <Fade in={open}>
            <Box sx={modalStyle}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#2d3748',
                  }}
                >
                  {isEditing ? "Editar Usuário" : "Cadastrar Novo Usuário"}
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: '#718096' }}>
                  <Close />
                </IconButton>
              </Box>

              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <Avatar
                  alt="Pré-visualização"
                  src={photoPreview}
                  sx={{ 
                    width: 120, 
                    height: 120,
                    mb: 2,
                    border: '4px solid #667eea',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                  }}
                />
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCamera />}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': {
                      borderColor: '#764ba2',
                      bgcolor: '#667eea10',
                    },
                  }}
                >
                  Carregar Foto
                  <input 
                    type="file" 
                    hidden 
                    accept="image/*"
                    onChange={handlePhotoChange} 
                  />
                </Button>
                <Typography variant="caption" sx={{ color: '#718096', mt: 1 }}>
                  JPG, PNG ou GIF (máx. 5MB)
                </Typography>
              </Box>

              <TextField
                label="Nome"
                fullWidth
                margin="normal"
                required
                value={newUser.firstName}
                onChange={(e) => {
                  setNewUser({ ...newUser, firstName: e.target.value });
                  if (errors.firstName) setErrors({ ...errors, firstName: null });
                }}
                error={!!errors.firstName}
                helperText={errors.firstName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Sobrenome"
                fullWidth
                margin="normal"
                required
                value={newUser.lastName}
                onChange={(e) => {
                  setNewUser({ ...newUser, lastName: e.target.value });
                  if (errors.lastName) setErrors({ ...errors, lastName: null });
                }}
                error={!!errors.lastName}
                helperText={errors.lastName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Email"
                fullWidth
                margin="normal"
                required={!isEditing}
                type="email"
                value={newUser.email}
                onChange={(e) => {
                  setNewUser({ ...newUser, email: e.target.value });
                  if (errors.email) setErrors({ ...errors, email: null });
                }}
                disabled={isEditing}
                error={!!errors.email}
                helperText={errors.email || (isEditing ? "Email não pode ser alterado" : "")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Telefone"
                fullWidth
                margin="normal"
                required
                placeholder="(11) 98765-4321"
                value={newUser.phone}
                onChange={(e) => {
                  setNewUser({ ...newUser, phone: e.target.value });
                  if (errors.phone) setErrors({ ...errors, phone: null });
                }}
                error={!!errors.phone}
                helperText={errors.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Nome da Empresa"
                fullWidth
                margin="normal"
                required
                value={newUser.company}
                onChange={(e) => {
                  setNewUser({ ...newUser, company: e.target.value });
                  if (errors.company) setErrors({ ...errors, company: null });
                }}
                error={!!errors.company}
                helperText={errors.company}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              {!isEditing && (
                <TextField
                  label="Senha"
                  fullWidth
                  margin="normal"
                  required
                  type="password"
                  value={newUser.password}
                  onChange={(e) => {
                    setNewUser({ ...newUser, password: e.target.value });
                    if (errors.password) setErrors({ ...errors, password: null });
                  }}
                  error={!!errors.password}
                  helperText={errors.password || "Mínimo de 6 caracteres"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#718096' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {/* Campo de Tipo de Usuário (Role) */}
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Tipo de Usuário</InputLabel>
                <Select
                  value={newUser.role}
                  label="Tipo de Usuário"
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#cbd5e0',
                    },
                  }}
                >
                  <MenuItem value="user">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person sx={{ color: '#48bb78' }} />
                      <Typography>Usuário Comum</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="admin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person sx={{ color: '#667eea' }} />
                      <Typography>Administrador</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Switch para Status Ativo/Inativo */}
              <FormControlLabel
                control={
                  <Switch
                    checked={newUser.isActive}
                    onChange={(e) => setNewUser({ ...newUser, isActive: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#48bb78',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#48bb78',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>Status:</Typography>
                    <Chip
                      label={newUser.isActive ? 'Ativo' : 'Inativo'}
                      size="small"
                      sx={{
                        bgcolor: newUser.isActive ? '#48bb78' : '#e53935',
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                }
                sx={{ mt: 2, mb: 1 }}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClose}
                  sx={{
                    py: 1.5,
                    borderColor: '#718096',
                    color: '#718096',
                    '&:hover': {
                      borderColor: '#2d3748',
                      bgcolor: '#f7fafc',
                    },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={isEditing ? handleSaveEditUser : handleAddUser}
                  disabled={submitLoading}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                    },
                    '&:disabled': {
                      background: '#cbd5e0',
                    },
                  }}
                >
                  {submitLoading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    <>
                      {isEditing ? (
                        <>
                          <CheckCircle sx={{ mr: 1 }} />
                          Salvar Alterações
                        </>
                      ) : (
                        <>
                          <Add sx={{ mr: 1 }} />
                          Adicionar Usuário
                        </>
                      )}
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: '#e53935' }} />
            Confirmar Exclusão
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir o usuário <strong>{deleteDialog.userName}</strong>? 
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleCloseDeleteDialog}
              sx={{ color: '#718096' }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteUser}
              variant="contained"
              sx={{
                bgcolor: '#e53935',
                '&:hover': { bgcolor: '#c62828' }
              }}
              startIcon={<Delete />}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para Feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Users;
